/**
 * Calendario nativo: vista mese con eventi propri (CRUD).
 * Backend: /calendar/events. Gli eventi estratti dalle email sono separati.
 *
 * Gestione fusi: gli eventi a orario usano l'ora locale (wall-clock) convertita
 * in UTC; gli eventi "tutto il giorno" sono ancorati alla DATA in UTC (start
 * 00:00:00Z, end 23:59:59Z) e letti/confrontati per data UTC, cosi' la data
 * memorizzata coincide con quella scelta a prescindere dal fuso.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  addMonths, subMonths, addDays, startOfDay, endOfDay, format, isSameMonth, isToday, parseISO,
} from 'date-fns';
import { it } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Plus, Loader2, Trash2, CalendarDays, AlertTriangle, RefreshCw, Cloud, CloudOff, Video } from 'lucide-react';
import { toast } from 'sonner';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { CalendarSuggestions } from '@/components/calendar/CalendarSuggestions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { calendarApi, videoApi, accountsApi, type CalendarEvent } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';

const PALETTE = ['#6366f1', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6', '#64748b'];
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

interface FormState {
  id?: string;
  title: string;
  startDate: string;  // yyyy-MM-dd
  endDate: string;    // yyyy-MM-dd
  startTime: string;  // HH:mm
  endTime: string;    // HH:mm
  allDay: boolean;
  location: string;
  description: string;
  color: string;
}

function emptyForm(day: Date): FormState {
  const d = format(day, 'yyyy-MM-dd');
  return {
    title: '', startDate: d, endDate: d, startTime: '09:00', endTime: '10:00',
    allDay: false, location: '', description: '', color: PALETTE[0]!,
  };
}

function eventToForm(ev: CalendarEvent): FormState {
  if (ev.all_day) {
    return {
      id: ev.id, title: ev.title,
      startDate: ev.start_at.slice(0, 10), endDate: ev.end_at.slice(0, 10),
      startTime: '09:00', endTime: '10:00', allDay: true,
      location: ev.location ?? '', description: ev.description ?? '', color: ev.color,
    };
  }
  const s = parseISO(ev.start_at);
  const e = parseISO(ev.end_at);
  return {
    id: ev.id, title: ev.title,
    startDate: format(s, 'yyyy-MM-dd'), endDate: format(e, 'yyyy-MM-dd'),
    startTime: format(s, 'HH:mm'), endTime: format(e, 'HH:mm'), allDay: false,
    location: ev.location ?? '', description: ev.description ?? '', color: ev.color,
  };
}

/** Calcola start_at/end_at ISO dal form (all-day → ancorato alla data UTC). */
function computeRange(f: FormState): { start_at: string; end_at: string } {
  if (f.allDay) {
    return { start_at: `${f.startDate}T00:00:00.000Z`, end_at: `${f.endDate}T23:59:59.999Z` };
  }
  return {
    start_at: new Date(`${f.startDate}T${f.startTime}`).toISOString(),
    end_at: new Date(`${f.endDate}T${f.endTime}`).toISOString(),
  };
}

export function CalendarPage() {
  const qc = useQueryClient();
  const [cursor, setCursor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm(new Date()));

  const monthStart = startOfMonth(cursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
  const days = useMemo(() => eachDayOfInterval({ start: gridStart, end: gridEnd }), [gridStart.getTime(), gridEnd.getTime()]);

  const fromIso = gridStart.toISOString();
  const toIso = addDays(gridEnd, 1).toISOString();
  const eventsQuery = useQuery({
    queryKey: ['calendar', fromIso, toIso],
    queryFn: () => calendarApi.range(fromIso, toIso),
  });
  const events = eventsQuery.data?.events ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ['calendar'] });

  // ---- Google Calendar (sync bidirezionale) ----
  const [params, setParams] = useSearchParams();
  const statusQuery = useQuery({ queryKey: ['google-status'], queryFn: () => calendarApi.googleStatus() });
  const connected = statusQuery.data?.connected ?? false;

  const syncMut = useMutation({
    mutationFn: (_opts?: { silent?: boolean }) => calendarApi.googleSync(),
    onSuccess: (r) => {
      invalidate();
      if (r.pulled || r.pushed || r.deleted) toast.success(`Sincronizzato: ↓${r.pulled} da Google, ↑${r.pushed} verso Google${r.deleted ? `, ${r.deleted} rimossi` : ''}`);
    },
    // L'auto-sync (silent) non mostra toast d'errore a ogni apertura della pagina
    onError: (err, opts) => { if (!opts?.silent) toast.error(err instanceof ApiError ? err.message : 'Sincronizzazione Google fallita'); },
  });

  // Auto-sync una sola volta quando risulta connesso (silenzioso sugli errori)
  const autoSynced = useRef(false);
  useEffect(() => {
    if (connected && !autoSynced.current) { autoSynced.current = true; syncMut.mutate({ silent: true }); }
  }, [connected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Esito redirect OAuth (?google_connected / ?google_error)
  useEffect(() => {
    if (params.get('google_connected')) {
      toast.success('Google Calendar collegato');
      statusQuery.refetch();
      params.delete('google_connected'); setParams(params, { replace: true });
    } else if (params.get('google_error')) {
      toast.error('Collegamento Google fallito: ' + params.get('google_error'));
      params.delete('google_error'); setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function connectGoogle() {
    try {
      const { auth_url } = await calendarApi.googleAuthUrl();
      window.location.href = auth_url;
    } catch {
      toast.error('Impossibile avviare il collegamento Google');
    }
  }

  const disconnectMut = useMutation({
    mutationFn: () => calendarApi.googleDisconnect(),
    onSuccess: () => { autoSynced.current = false; statusQuery.refetch(); toast.success('Google Calendar scollegato'); },
  });

  const range = computeRange(form);
  const invalidRange = new Date(range.end_at) < new Date(range.start_at);

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        location: form.location.trim() || undefined,
        start_at: range.start_at,
        end_at: range.end_at,
        all_day: form.allDay,
        color: form.color,
      };
      return form.id ? calendarApi.update(form.id, payload) : calendarApi.create(payload);
    },
    onSuccess: () => { invalidate(); setOpen(false); toast.success(form.id ? 'Evento aggiornato' : 'Evento creato'); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Errore salvataggio'),
  });

  const deleteMut = useMutation({
    mutationFn: () => calendarApi.remove(form.id!),
    onSuccess: () => { invalidate(); setOpen(false); toast.success('Evento eliminato'); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Errore eliminazione'),
  });

  function openCreate(day: Date) { setForm(emptyForm(day)); setOpen(true); }
  function openEdit(ev: CalendarEvent) { setForm(eventToForm(ev)); setOpen(true); }

  function eventsForDay(day: Date): CalendarEvent[] {
    const dayKey = format(day, 'yyyy-MM-dd');
    const s = startOfDay(day).getTime();
    const e = endOfDay(day).getTime();
    return events.filter((ev) => {
      if (ev.all_day) {
        // confronto per data UTC memorizzata (stabile rispetto al fuso)
        return ev.start_at.slice(0, 10) <= dayKey && ev.end_at.slice(0, 10) >= dayKey;
      }
      return parseISO(ev.start_at).getTime() <= e && parseISO(ev.end_at).getTime() >= s;
    });
  }

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold flex items-center gap-2 capitalize">
                <CalendarDays className="h-5 w-5 text-primary" />
                {format(cursor, 'MMMM yyyy', { locale: it })}
              </h1>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCursor(subMonths(cursor, 1))}><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => setCursor(new Date())}>Oggi</Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCursor(addMonths(cursor, 1))}><ChevronRight className="h-4 w-4" /></Button>
              </div>
              {eventsQuery.isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
            <div className="flex items-center gap-2">
              {connected ? (
                <>
                  <Button variant="outline" size="sm" onClick={() => syncMut.mutate(undefined)} disabled={syncMut.isPending} title="Sincronizza con Google Calendar">
                    <RefreshCw className={cn('h-4 w-4 mr-1.5', syncMut.isPending && 'animate-spin')} /> Sincronizza
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" title="Scollega Google Calendar" onClick={() => disconnectMut.mutate()}>
                    <CloudOff className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={connectGoogle}>
                  <Cloud className="h-4 w-4 mr-1.5" /> Collega Google Calendar
                </Button>
              )}
              <Button onClick={() => openCreate(new Date())}><Plus className="h-4 w-4 mr-1" /> Nuovo evento</Button>
            </div>
          </div>

          <CalendarSuggestions />

          {eventsQuery.isError && (
            <div className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
              <span className="inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Impossibile caricare gli eventi.</span>
              <Button variant="ghost" size="sm" onClick={() => eventsQuery.refetch()}>Riprova</Button>
            </div>
          )}

          <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden border">
            {WEEKDAYS.map((d) => (
              <div key={d} className="bg-muted/50 text-center text-xs font-semibold text-muted-foreground py-2">{d}</div>
            ))}
            {days.map((day) => {
              const inMonth = isSameMonth(day, cursor);
              const dayEvents = eventsForDay(day);
              return (
                <div
                  key={day.toISOString()}
                  onClick={() => openCreate(day)}
                  className={cn(
                    'bg-background min-h-[96px] p-1.5 cursor-pointer hover:bg-muted/40 transition-colors flex flex-col gap-1',
                    !inMonth && 'bg-muted/20 text-muted-foreground'
                  )}
                >
                  <div className="flex justify-end">
                    <span className={cn(
                      'text-xs h-5 w-5 flex items-center justify-center rounded-full',
                      isToday(day) && 'bg-primary text-primary-foreground font-bold'
                    )}>{format(day, 'd')}</span>
                  </div>
                  <div className="flex flex-col gap-0.5 overflow-hidden">
                    {dayEvents.slice(0, 3).map((ev) => (
                      <button
                        key={ev.id}
                        onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                        className="text-[11px] text-left truncate rounded px-1 py-0.5 text-white"
                        style={{ backgroundColor: ev.color }}
                        title={ev.title}
                      >
                        {!ev.all_day && <span className="opacity-90 mr-1">{format(parseISO(ev.start_at), 'HH:mm')}</span>}
                        {ev.title}
                      </button>
                    ))}
                    {dayEvents.length > 3 && (
                      <span className="text-[10px] text-muted-foreground px-1">+{dayEvents.length - 3} altri</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      {/* Dialog crea/modifica */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Modifica evento' : 'Nuovo evento'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="ev-title">Titolo</Label>
              <Input id="ev-title" value={form.title} autoFocus onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Call con cliente" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.allDay} onChange={(e) => setForm({ ...form, allDay: e.target.checked })} />
              Tutto il giorno
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ev-sdate">Inizio{form.allDay ? '' : ' (data)'}</Label>
                <Input id="ev-sdate" type="date" value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value, endDate: form.endDate < e.target.value ? e.target.value : form.endDate })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ev-edate">Fine{form.allDay ? '' : ' (data)'}</Label>
                <Input id="ev-edate" type="date" value={form.endDate} min={form.startDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
              </div>
            </div>
            {!form.allDay && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ev-stime">Ora inizio</Label>
                  <Input id="ev-stime" type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ev-etime">Ora fine</Label>
                  <Input id="ev-etime" type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
                </div>
              </div>
            )}
            {invalidRange && (
              <p className="text-xs text-destructive">La fine non può precedere l'inizio.</p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="ev-loc">Luogo</Label>
              <Input id="ev-loc" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Opzionale" />
            </div>
            {/* Videochiamata: crea la stanza white-label e mette il link sull'evento */}
            <VideoCallField form={form} onLink={(url) => setForm({ ...form, location: url })} />

            <div className="space-y-1.5">
              <Label htmlFor="ev-desc">Note</Label>
              <textarea id="ev-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2} className="w-full text-sm rounded-md border bg-background px-3 py-2 outline-none focus:ring-1 focus:ring-primary" placeholder="Opzionale" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Colore</Label>
              {PALETTE.map((c) => (
                <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                  className={cn('h-5 w-5 rounded-full border', form.color === c && 'ring-2 ring-offset-1 ring-foreground')}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            {form.id && (
              <Button variant="ghost" className="text-destructive mr-auto" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
                <Trash2 className="h-4 w-4 mr-1" /> Elimina
              </Button>
            )}
            <Button onClick={() => saveMut.mutate()} disabled={!form.title.trim() || invalidRange || saveMut.isPending}>
              {saveMut.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Campo "Videochiamata": crea al volo una stanza white-label e mette il link
 * nell'evento (campo Luogo), così compare anche negli inviti .ics e su Google.
 */
function VideoCallField({ form, onLink }: { form: FormState; onLink: (url: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [guest, setGuest] = useState('');
  const statusQuery = useQuery({ queryKey: ['video-status'], queryFn: () => videoApi.status(), retry: false });
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => accountsApi.list() });
  const existing = /\/call\//.test(form.location) ? form.location : null;

  if (!statusQuery.data?.available) return null;

  const accounts = accountsQuery.data?.accounts ?? [];
  const accountId = (accounts.find((a) => a.is_default) ?? accounts[0])?.id;

  async function create() {
    setBusy(true);
    try {
      const emails = guest.split(/[,;\s]+/).map((e) => e.trim()).filter((e) => e.includes('@'));
      const { url, invited, recipients } = await videoApi.create({
        title: form.title.trim() || 'Videochiamata',
        accountId,
        startsAt: new Date(`${form.startDate}T${form.startTime}`).toISOString(),
        endsAt: new Date(`${form.endDate}T${form.endTime}`).toISOString(),
        guestEmails: emails.length ? emails : undefined,
      });
      onLink(url);
      if (recipients === 0) toast.success('Videochiamata creata: il link è nell’evento');
      else if (invited === recipients) toast.success(`Videochiamata creata · ${invited} invit${invited === 1 ? 'o inviato' : 'i inviati'}`);
      else toast.warning(`Videochiamata creata, ma ${recipients - invited} invit${recipients - invited === 1 ? 'o non è partito' : 'i non sono partiti'}`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Creazione videochiamata fallita');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Video className="h-4 w-4 text-indigo-500" /> Videochiamata
        </div>
        {existing ? (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => { void navigator.clipboard.writeText(existing); toast.success('Link copiato'); }}
              className="text-xs text-primary hover:underline">Copia link</button>
            <button type="button" onClick={() => onLink('')} className="text-xs text-muted-foreground hover:text-destructive">Rimuovi</button>
          </div>
        ) : (
          <Button type="button" size="sm" variant="outline" onClick={() => void create()} disabled={busy}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Video className="h-3.5 w-3.5 mr-1.5" />}
            Aggiungi
          </Button>
        )}
      </div>
      {existing ? (
        <p className="text-xs text-muted-foreground break-all">{existing}</p>
      ) : (
        <>
          <Input
            value={guest}
            onChange={(e) => setGuest(e.target.value)}
            placeholder="Email invitati, separate da virgola (facoltativo)"
            className="h-8 text-xs"
          />
          <p className="text-xs text-muted-foreground">Stanza sul tuo dominio, condivisibile con chiunque.</p>
        </>
      )}
    </div>
  );
}
